import { useEffect } from 'react';
import { analytics } from '../../../firebase';
import { logEvent } from 'firebase/analytics';

export default function DutyVigilLegal() {
  const pathname = window.location.pathname;
  const isPrivacyOnly = pathname.includes('/privacy');

  useEffect(() => {
    logEvent(analytics, 'page_view', {
      page_title: isPrivacyOnly ? 'Privacy Policy - DutyVigil' : 'Legal - DutyVigil',
      page_location: window.location.href,
      page_path: window.location.pathname
    });
    window.scrollTo(0, 0);
  }, [isPrivacyOnly]);

  const primaryColor = '#5B7742';
  const linkColor = '#556B2F';
  const backgroundColor = '#f8f9fa';
  const darkTextColor = '#333333';
  const lightTextColor = '#666666';
  const borderColor = '#e9ecef';

  const sectionHeaderStyle = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: `1px solid ${borderColor}`
  };

  const subHeaderStyle = {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '12px',
    marginTop: '24px'
  };

  const cardStyle = {
    padding: '24px',
    backgroundColor: backgroundColor,
    borderRadius: '8px',
    border: `1px solid ${borderColor}`,
    marginBottom: '32px'
  };

  const headingStyle = {
    fontWeight: '300',
    color: '#1a1a1a'
  };

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundColor: '#ffffff',
      color: darkTextColor,
      lineHeight: '1.6',
      fontSize: '16px'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: backgroundColor,
        padding: '40px 20px',
        textAlign: 'center',
        borderBottom: `1px solid ${borderColor}`
      }}>
        <img
          src="/apps/dutyvigil/logo.png"
          alt="DutyVigil Logo"
          style={{
            width: '64px',
            height: '64px',
            marginBottom: '20px',
            display: 'block',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}
        />
        <h1 style={{ ...headingStyle, fontSize: '2.5rem', margin: '0 0 8px 0' }}>
          {isPrivacyOnly ? 'Privacy Policy' : 'Master Software Agreement'}
        </h1>
        <p style={{
          fontSize: '18px',
          color: primaryColor,
          margin: '0 0 8px 0',
          fontWeight: '500'
        }}>
          DutyVigil - Fatigue-Aware Duty Allocation
        </p>
        <p style={{
          fontSize: '14px',
          color: lightTextColor,
          margin: '0'
        }}>
          Version 1.1.0 | Effective Date: Jan 26, 2026
        </p>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: '720px',
        margin: '0 auto',
        padding: '32px 20px'
      }}>
        
        {!isPrivacyOnly && (
          <div style={{ ...cardStyle, marginBottom: '48px', textAlign: 'center', backgroundColor: 'rgba(91, 119, 66, 0.05)' }}>
            <p style={{ margin: 0, fontWeight: '500', color: primaryColor }}>
              PLEASE READ THIS AGREEMENT CAREFULLY. BY USING THE SOFTWARE, YOU ARE BOUND BY THE SOFTWARE LICENSE AGREEMENT, THE TERMS OF USE, AND THE PRIVACY POLICY CONTAINED HEREIN.
            </p>
          </div>
        )}

        {!isPrivacyOnly && (
          <>
            {/* LICENSE AGREEMENT */}
            <section id="license" style={{ marginBottom: '64px' }}>
              <h2 style={{ ...headingStyle, ...sectionHeaderStyle, fontSize: '1.75rem' }}>PART A: Software License Agreement</h2>
              
              <div style={cardStyle}>
                <div style={{ marginBottom: '8px' }}><strong>Software:</strong> DutyVigil v1.1.0</div>
                <div><strong>Licensor:</strong> Tattva Dynamics</div>
              </div>

              <h3 style={{ ...headingStyle, ...subHeaderStyle }}>1. Ownership</h3>
              <p style={{ color: '#495057' }}>
                Tattva Dynamics retains all ownership and intellectual property rights to the Software. This Agreement does not grant you ownership of the Software.
              </p>

              <h3 style={{ ...headingStyle, ...subHeaderStyle }}>2. Permitted Use</h3>
              <p style={{ color: '#495057' }}>
                You are permitted to install and use the Software and its associated features on a single computer or device for organizational or administrative purposes. The license is tied to a specific Machine ID.
              </p>

              <h3 style={{ ...headingStyle, ...subHeaderStyle }}>3. Activation</h3>
              <p style={{ color: '#495057' }}>
                Full access to all Software features requires a valid License Key. Use of the Software without activation is limited to the Free Trial period.
              </p>

              <h3 style={{ ...headingStyle, ...subHeaderStyle }}>4. Disclaimer of Warranty</h3>
              <p style={{ fontWeight: '600', color: '#1a1a1a' }}>
                THE SOFTWARE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND, EITHER EXPRESSED OR IMPLIED.
              </p>
            </section>

            {/* TERMS OF USE */}
            <section id="terms" style={{ marginBottom: '64px' }}>
              <h2 style={{ ...headingStyle, ...sectionHeaderStyle, fontSize: '1.75rem' }}>PART B: Terms of Use Agreement</h2>
              <p style={{ fontSize: '14px', color: lightTextColor, fontStyle: 'italic', marginBottom: '24px' }}>
                PLEASE READ THIS TERMS OF USE AGREEMENT ("AGREEMENT") CAREFULLY BEFORE USING DUTYVIGIL. BY USING THE SOFTWARE, YOU ARE AGREEING TO BE BOUND BY THE TERMS OF THIS AGREEMENT.
              </p>

              <h3 style={{ ...headingStyle, ...subHeaderStyle }}>1. License Grant</h3>
              <p style={{ color: '#495057' }}>
                Tattva Dynamics grants you a non-exclusive, non-transferable, limited license to use the DutyVigil application and its associated features for organizational and administrative management purposes.
              </p>

              <h3 style={{ ...headingStyle, ...subHeaderStyle }}>2. Trial Period</h3>
              <p style={{ color: '#495057' }}>
                The Software is provided with a 30-day Free Trial period starting from the date of first installation. After this period, the Software will cease to function unless a valid License Key is entered.
              </p>

              <h3 style={{ ...headingStyle, ...subHeaderStyle }}>3. Restrictions</h3>
              <ul style={{ paddingLeft: '20px', color: '#495057' }}>
                <li style={{ marginBottom: '8px' }}>Decompile, reverse engineer, or attempt to derive the source code of the Software.</li>
                <li style={{ marginBottom: '8px' }}>Use the Software for any purpose that violates any local or international law.</li>
                <li style={{ marginBottom: '8px' }}>Distribute or sub-license the Software to third parties without prior written consent from Tattva Dynamics.</li>
              </ul>

              <h3 style={{ ...headingStyle, ...subHeaderStyle }}>4. Limitation of Liability</h3>
              <p style={{ color: '#495057' }}>
                Tattva Dynamics shall not be liable for any damages arising out of the use or inability to use the Software, including but not limited to scheduling errors or assessment inaccuracies.
              </p>

              <h3 style={{ ...headingStyle, ...subHeaderStyle }}>5. Termination</h3>
              <p style={{ color: '#495057' }}>
                This license is effective until terminated. Your rights under this license will terminate automatically without notice if you fail to comply with any term(s) of this agreement.
              </p>
            </section>
          </>
        )}

        {/* PRIVACY POLICY */}
        <section id="privacy" style={{ marginBottom: isPrivacyOnly ? '0' : '64px' }}>
          <h2 style={{ ...headingStyle, ...sectionHeaderStyle, fontSize: '1.75rem' }}>
            {isPrivacyOnly ? 'Privacy Policy' : 'PART C: Privacy Policy'}
          </h2>
          
          <div style={cardStyle}>
            <p style={{ margin: 0, color: '#495057' }}>
              At Tattva Dynamics, we prioritize your data sovereignty. DutyVigil is designed as an <strong>offline-first</strong> application to ensure that your sensitive organizational data never leaves your controlled environment.
            </p>
          </div>

          <h3 style={{ ...headingStyle, ...subHeaderStyle }}>1. Data Ownership</h3>
          <p style={{ color: '#495057' }}>
            You retain full and exclusive ownership of all data, records, and information entered into or generated by the Software ("User Data"). Tattva Dynamics claims no rights, title, or interest in your data. <strong>User Data</strong> includes, but is not limited to, account credentials (usernames and hashed passwords), duty assignments, employee availability, fatigue metrics, local application logs, and database integrity checksums.
          </p>

          <h3 style={{ ...headingStyle, ...subHeaderStyle }}>2. Data Portability</h3>
          <p style={{ color: '#495057' }}>
            You have the right to export your User Data in standard formats (e.g., Excel, CSV, JSON) at any time. This right to your data is absolute and persists even after the expiration of a trial period or a paid license.
          </p>

          <h3 style={{ ...headingStyle, ...subHeaderStyle }}>3. Local Storage & Zero-Access</h3>
          <p style={{ color: '#495057' }}>
            DutyVigil operates entirely on your host machine. All User Data is stored in a local SQLite database within your system's application data directory. Tattva Dynamics does not have access to your local database, your Machine ID, or any User Data processed by the application.
          </p>

          <h3 style={{ ...headingStyle, ...subHeaderStyle }}>4. Security & Encryption Disclosure</h3>
          <p style={{ color: '#495057' }}>
            As DutyVigil is intended for deployment in secure, air-gapped, or physically protected environments, the application does not apply application-level encryption to the local database file.
          </p>
          <div style={{ ...cardStyle, marginTop: '16px', backgroundColor: 'rgba(139, 46, 46, 0.05)', borderColor: 'rgba(139, 46, 46, 0.2)', borderLeftWidth: '5px', borderLeftColor: '#8B2E2E' }}>
            <strong style={{ color: '#8B2E2E' }}>User Responsibility:</strong> You are solely responsible for:
            <ul style={{ marginTop: '8px', paddingLeft: '20px', color: '#495057' }}>
              <li>Implementing operating-system-level disk encryption (e.g., BitLocker, FileVault, or LUKS).</li>
              <li>Maintaining physical security of the host machine.</li>
              <li>Managing user access controls and password strength within the application.</li>
            </ul>
          </div>

          <h3 style={{ ...headingStyle, ...subHeaderStyle }}>5. Third-Party Services</h3>
          <p style={{ color: '#495057' }}>
            The Software does not connect to any third-party services, analytics engines, or cloud storage providers. No telemetry or "phone home" data is collected.
          </p>
          </section>

          {/* Contact Section */}
        <div style={{
          padding: '24px',
          marginTop: '16px',
          backgroundColor: backgroundColor,
          borderRadius: '8px',
          border: `1px solid ${borderColor}`,
          marginBottom: '32px'
        }}>
          <h2 style={{ ...headingStyle, ...sectionHeaderStyle, borderBottom: 'none', marginBottom: '8px' }}>
            Contact Us
          </h2>
          <p style={{ color: '#495057', margin: '0 0 16px 0' }}>
            If you have any questions about these legal terms or DutyVigil's practices, please contact us:
          </p>
          <div style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#1a1a1a' }}>Email:</strong>
            <a 
              href="mailto:support@tattvadynamics.com" 
              style={{
                color: linkColor,
                textDecoration: 'none',
                marginLeft: '8px',
                fontWeight: '500'
              }}
            >
              support@tattvadynamics.com
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        textAlign: 'center',
        padding: '24px 20px',
        backgroundColor: backgroundColor,
        borderTop: `1px solid ${borderColor}`,
        color: lightTextColor,
        fontSize: '14px'
      }}>
        <p style={{ margin: '0 0 8px 0' }}>
          © 2026 Tattva Dynamics. All rights reserved.
        </p>
        <p style={{ margin: '0', fontSize: '12px' }}>
          DutyVigil is a trademark of Tattva Dynamics.
        </p>
      </div>
    </div>
  );
}
